/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Open Source Version is distributed with Sencha Ext JS pursuant to a FLOSS Exception agreed upon
 * between Sonatype, Inc. and Sencha Inc. Sencha Ext JS is licensed under GPL v3 and cannot be redistributed as part of a
 * closed source work.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import {useEffect, useRef, useState} from 'react';
import {interpret} from 'xstate';

import {PRO_FAMILY_EDITIONS, resolveEditionId} from '../editionUtils';
import AppStateMachine, {MAX_DISCONNECT_WARNINGS, contextFromSeed} from './AppStateMachine';
import {createPermissionCheck} from './permissions';
import {deleteSession} from './stateApi';

/**
 * Nexus One state (NEXUS-55433): the same state and permission methods as the ExtJS facade, read from the
 * AppStateMachine instead of NX.State and NX.Permissions.
 *
 * nexus-ui-plugin is bundled into every plugin bundle, so module state is per bundle. The one machine instance is
 * registered on window and every copy of this module reads it from there.
 */

/** Global that app.js (app.vm) assigns the state seed to. */
export const STATE_SEED_VARIABLE = 'NX_STATE_SEED';

/** Seed key of the flag that turns the machine on (FeatureFlags.NEXUS_ONE_STATE_MACHINE_ENABLED). */
export const STATE_MACHINE_FLAG = 'nexus.one.state.machine.enabled';

const REGISTRY_VARIABLE = '__nxAppState';

const SEED_LISTENERS_VARIABLE = '__nxStateSeedListeners';

const RECONNECT_REQUESTED_EVENT = 'nexus:server-reconnect-requested';

// The Preview UI disconnect modal (serverDisconnectedBroadcast.ts) opens and closes on these
const DISCONNECTED_EVENT = 'nexus:server-disconnected';

const RECONNECTED_EVENT = 'nexus:server-reconnected';

function isPreviewUi() {
  return window.location.hash.startsWith('#preview');
}

/**
 * Tells the Preview UI disconnect modal about the machine's own poll, as NX.controller.State does for its poll:
 * disconnected on each failure past MAX_DISCONNECT_WARNINGS (also when a Retry fails), reconnected on the first
 * success after that. Without it, a machine poll that fails while ExtJS's still works would never open the modal,
 * and a Retry that the machine's poll answers would never close it. Preview UI only, like ExtJS; Classic shows its
 * own ExtJS dialog.
 */
function announceConnection(next, old) {
  const wasDisconnected = old.failures > MAX_DISCONNECT_WARNINGS;
  let event;
  if (next.failures > MAX_DISCONNECT_WARNINGS && next.failures !== old.failures) {
    event = DISCONNECTED_EVENT;
  }
  else if (wasDisconnected && next.failures === 0) {
    event = RECONNECTED_EVENT;
  }
  if (event && isPreviewUi()) {
    window.dispatchEvent(new CustomEvent(event));
  }
}

function registry() {
  return window[REGISTRY_VARIABLE];
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

/**
 * Calls back with the state seed as soon as app.js assigns it. app.js loads after the plugin bundles, so the seed
 * does not exist yet when a bundle first runs. The callback runs synchronously inside the assignment, before app.js
 * goes on to start ExtJS, so no React view (Nexus One or one hosted by ExtJS) can render before the machine exists.
 * If app.js never loads, the callback never runs and the existing REST fallback stays in charge.
 * @param {function(Object)} callback
 */
export function waitForStateSeed(callback) {
  const seed = window[STATE_SEED_VARIABLE];
  if (seed) {
    callback(seed);
    return;
  }

  // Shared by every bundle's copy of this module
  if (!window[SEED_LISTENERS_VARIABLE]) {
    window[SEED_LISTENERS_VARIABLE] = [];
    let value;
    Object.defineProperty(window, STATE_SEED_VARIABLE, {
      configurable: true,
      enumerable: true,
      get: () => value,
      set: (assigned) => {
        value = assigned;
        const listeners = window[SEED_LISTENERS_VARIABLE];
        window[SEED_LISTENERS_VARIABLE] = [];
        listeners.forEach((listener) => {
          try {
            listener(assigned);
          }
          catch (error) {
            // never break app.js, which still has to start ExtJS
            console.error('[AppState] State seed listener failed:', error);
          }
        });
      }
    });
  }
  window[SEED_LISTENERS_VARIABLE].push(callback);
}

/**
 * @param {Object} seed the app.js state seed
 * @returns {boolean} true if the seed turns the state machine on
 */
export function isEnabledBySeed(seed) {
  const entry = seed?.[STATE_MACHINE_FLAG];
  return (entry && typeof entry === 'object' ? entry.value : entry) === true;
}

/**
 * Creates, seeds and starts the machine once per page, and registers it for imperative callers. Does nothing when
 * the flag in the seed is off.
 * @param {Object} seed the app.js state seed
 * @returns {boolean} true if the machine is running
 */
export function startAppState(seed) {
  if (registry()) {
    return true;
  }
  if (!isEnabledBySeed(seed)) {
    return false;
  }

  const listeners = new Set();
  const service = interpret(AppStateMachine.withContext(contextFromSeed(seed)));
  let previous = service.initialState.context;

  service.onTransition((state) => {
    const {context} = state;
    if (context === previous) {
      return;
    }
    const old = previous;
    previous = context;
    announceConnection(context, old);
    listeners.forEach((listener) => listener(context, old));
  });

  // The Preview UI disconnect modal's Retry button
  const onReconnectRequested = () => service.send('REFRESH');
  window.addEventListener(RECONNECT_REQUESTED_EVENT, onReconnectRequested);

  window[REGISTRY_VARIABLE] = {
    service,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    // set by followExtJsUser()
    unfollowExtJsUser: undefined,
    stop() {
      this.unfollowExtJsUser?.();
      window.removeEventListener(RECONNECT_REQUESTED_EVENT, onReconnectRequested);
      service.stop();
      listeners.clear();
      delete window[REGISTRY_VARIABLE];
    }
  };

  service.start();
  return true;
}

/**
 * Copies user changes ExtJS makes on its own into the machine: sign-out from the ExtJS menu, the ExtJS idle
 * session timeout, sign-in from the ExtJS login dialog and user changes ExtJS's own poll sees. Without this, the
 * machine would keep the signed-out user and their permissions until its next poll, or for good when polling is
 * off (statusIntervalAuthenticated 0). Call once ExtJS has launched. Does nothing if the machine is not running, if
 * ExtJS has no State controller, or if it is already following.
 * @returns {boolean} true if the machine follows ExtJS's user
 */
export function followExtJsUser() {
  const entry = registry();
  if (!entry) {
    return false;
  }
  if (entry.unfollowExtJsUser) {
    return true;
  }
  let stateController;
  try {
    stateController = window.Ext?.getApplication?.()?.getController?.('State');
  }
  catch {
    // ExtJS application has not launched
  }
  if (!stateController?.on) {
    return false;
  }
  const onUserChanged = (user) => {
    if (registry() === entry && userIdentity(user) !== userIdentity(getValue('user'))) {
      setUser(user);
    }
  };
  stateController.on('userchanged', onUserChanged);
  entry.unfollowExtJsUser = () => stateController.un?.('userchanged', onUserChanged);
  return true;
}

/** Stops and unregisters the machine. For tests. */
export function stopAppState() {
  registry()?.stop();
}

/** @returns {boolean} true when the machine is running and the facade reads from it */
export function isActive() {
  return Boolean(registry());
}

/** @returns {Object|undefined} the running machine's interpreter, for imperative callers */
export function getService() {
  return registry()?.service;
}

/** The running machine's registry entry; throws a clear error when the machine is not running. */
function active() {
  const entry = registry();
  if (!entry) {
    throw new Error('[AppState] The state machine is not running; check AppState.isActive() first');
  }
  return entry;
}

function context() {
  return active().service.getSnapshot().context;
}

// ---------------------------------------------------------------------------
// Change notification, with the ExtJS controller event names
// ---------------------------------------------------------------------------

function userIdentity(user) {
  return user ? `user:${user.id}` : 'anonymous';
}

/**
 * The state keys whose value changed, as NX.controller.State reports them. A new value for the same user id is
 * applied silently there, so 'user' only counts when the user appears, disappears or changes id.
 */
function changedKeys(next, old) {
  const keys = new Set([...Object.keys(next.values), ...Object.keys(old.values)]);
  return [...keys].filter((key) => {
    if (next.values[key] === old.values[key]) {
      return false;
    }
    return key !== 'user' || userIdentity(next.values.user) !== userIdentity(old.values.user);
  });
}

/**
 * Same contract as ExtJS.onStateChange: events are '<key>changed' (lower case) or 'changed'. 'changed' listeners
 * get (key, value, oldValue), '<key>changed' listeners get (value, oldValue).
 * @returns {function} unsubscribe
 */
export function onStateChange(events, listener) {
  const wanted = new Set(events);
  return active().subscribe((next, old) => {
    changedKeys(next, old).forEach((key) => {
      if (wanted.has(`${key.toLowerCase()}changed`)) {
        listener(next.values[key], old.values[key]);
      }
      if (wanted.has('changed')) {
        listener(key, next.values[key], old.values[key]);
      }
    });
  });
}

/**
 * Same contract as ExtJS.onPermissionsChange: called after each permission load.
 * @returns {function} unsubscribe
 */
export function onPermissionsChange(listener) {
  return active().subscribe((next, old) => {
    if (next.permissionsLoads !== old.permissionsLoads) {
      listener();
    }
  });
}

/** Calls the listener after any state or permission change. */
function onAnyChange(listener) {
  return active().subscribe((next, old) => {
    if (next.permissionsLoads !== old.permissionsLoads || changedKeys(next, old).length > 0) {
      listener();
    }
  });
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

function getValue(key, defaultValue) {
  const value = context().values[key];
  return value !== undefined ? value : defaultValue;
}

/**
 * Writes a value locally. ExtJS keeps its own copy of state, so the value is written there as well, as callers of
 * ExtJS.state().setValue() expect.
 */
function setValue(key, value) {
  active().service.send({type: 'SET_VALUE', key, value});
  try {
    window.NX?.State?.setValue?.(key, value);
  }
  catch {
    // ExtJS not launched; its next poll catches up
  }
}

/** Sets the user, as NX.State.setUser() does after sign-in and sign-out. */
export function setUser(user) {
  active().service.send({type: 'SET_USER', user});
}

/** Polls state now instead of waiting for the interval. */
export function refreshNow() {
  active().service.send('REFRESH');
}

const stateApi = {
  getValue,
  setValue,
  setValues: (values) => Object.entries(values).forEach(([key, value]) => setValue(key, value)),
  refreshNow,
  getUser: () => getValue('user'),
  setUser,
  getEdition: () => getValue('status')?.edition,
  getVersion: () => getValue('status')?.version,
  getBuildRevision: () => getValue('status')?.buildRevision,
  getBuildTimestamp: () => getValue('status')?.buildTimestamp,
  isReceiving: () => getValue('receiving'),
  isBrowserSupported: () => getValue('browserSupported') === true,
  requiresLicense: () => getValue('license', {}).required === true,
  isLicenseInstalled: () => getValue('license', {}).installed === true,
  isLicenseValid: () => getValue('license', {}).installed === true && getValue('license', {}).valid === true,
  getDaysToLicenseExpiry: () => getValue('license', {}).daysToExpiry,
  hasFeature: (feature) => Boolean(getValue('license', {}).features?.includes(feature)),
  isLicensedEdition: () => isLicensedEdition(),
  isEnterpriseEdition: () => isEnterpriseEdition(),
  // NX.app.Application.bundleActive(), answered from the machine's own activeBundles (BundleStateContributor) so it
  // holds before ExtJS boots. activeBundles lists jar names such as 'nexus-foo-3.99.0.jar', so match by substring as
  // ExtJS does.
  isBundleActive: (symbolicName) => getValue('activeBundles', []).some((item) => item.includes(symbolicName)),
  // NX.State.isClustered(): the hazelcast plugin bundle is active and nodes state reports clustering enabled.
  isClustered: () => stateApi.isBundleActive('nexus-hazelcast-plugin') && getValue('nodes', {}).enabled === true
};

/** @returns {Object} the machine's state, with the NX.State methods the facade's callers use */
export function state() {
  return stateApi;
}

/** @returns {boolean} true if the edition is any Pro-family tier, as ExtJS.isLicensedEdition() */
export function isLicensedEdition() {
  return PRO_FAMILY_EDITIONS.includes(resolveEditionId(stateApi));
}

/** Alias of isLicensedEdition. */
export const isProEdition = isLicensedEdition;

/** @returns {boolean} true on Enterprise, as ExtJS.isEnterpriseEdition() */
export function isEnterpriseEdition() {
  return getValue('editionId') === 'nexus-enterprise-edition';
}

/** @returns {boolean} true if a user is signed in */
export function hasUser() {
  return Boolean(getValue('user'));
}

/**
 * True when "is a user signed in" has an answer: a user is known, a poll has landed, or no poll is coming.
 */
export function isAuthStateResolved() {
  if (hasUser() || getValue('receiving') !== false) {
    return true;
  }
  const snapshot = active().service.getSnapshot();
  return snapshot.matches({stateValues: 'paused'}) || snapshot.matches({stateValues: 'disconnected'});
}

/**
 * Resolves once isAuthStateResolved() holds, or after timeoutMs. Polls now instead of waiting for the interval.
 * @returns {Promise<void>}
 */
export function whenAuthStateResolved(timeoutMs) {
  if (isAuthStateResolved()) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    let timer;
    const unsubscribe = active().subscribe(() => {
      if (isAuthStateResolved()) {
        finish();
      }
    });
    function finish() {
      clearTimeout(timer);
      unsubscribe();
      resolve();
    }
    timer = setTimeout(finish, timeoutMs);
    refreshNow();
  });
}

// ---------------------------------------------------------------------------
// Permissions
// ---------------------------------------------------------------------------

let checkedPermissions;
let check = createPermissionCheck(undefined);

/** @returns {boolean} true if the user has the permission; false until permissions load */
export function checkPermission(permission) {
  const {permissions} = context();
  if (permissions !== checkedPermissions) {
    checkedPermissions = permissions;
    check = createPermissionCheck(permissions);
  }
  return check(permission);
}

/** @returns {boolean} true once a permission set is loaded */
export function arePermissionsReady() {
  return context().permissions !== undefined;
}

/** @returns {boolean} true if the permissions region has failed its load */
function havePermissionsFailed() {
  return active().service.getSnapshot().matches({permissions: 'failed'});
}

/** Resolves once permissions are loaded; rejects on a failed load or after timeoutMs, whichever comes first. */
export function waitForPermissions(timeoutMs) {
  if (arePermissionsReady()) {
    return Promise.resolve();
  }
  if (havePermissionsFailed()) {
    return Promise.reject(new Error('Permissions failed to load'));
  }
  return new Promise((resolve, reject) => {
    const unsubscribe = onPermissionsChange(() => {
      if (arePermissionsReady()) {
        clearTimeout(timer);
        unsubscribe();
        resolve();
        return;
      }
      if (havePermissionsFailed()) {
        clearTimeout(timer);
        unsubscribe();
        reject(new Error('Permissions failed to load'));
      }
    });
    const timer = setTimeout(() => {
      unsubscribe();
      reject(new Error('Permissions load timed out'));
    }, timeoutMs);
  });
}

/** Resolves on the next permission load; rejects after timeoutMs. */
export function waitForNextPermissionChange(timeoutMs) {
  return new Promise((resolve, reject) => {
    const unsubscribe = onPermissionsChange(() => {
      clearTimeout(timer);
      unsubscribe();
      resolve();
    });
    const timer = setTimeout(() => {
      unsubscribe();
      reject(new Error('timed out waiting for permissions to update'));
    }, timeoutMs);
  });
}

// ---------------------------------------------------------------------------
// Sign-out
// ---------------------------------------------------------------------------

/** True for http(s) URLs, absolute or relative to the page; false for javascript:, data: and the like. */
function isNavigableUrl(url) {
  try {
    const {protocol} = new URL(url, window.location.href);
    return protocol === 'http:' || protocol === 'https:';
  }
  catch {
    return false;
  }
}

/**
 * Deletes the session as NX.controller.User.signOut() does, then clears the user in the machine and in ExtJS.
 * Follows a Location header (SSO logout) instead.
 */
export async function signOut() {
  let location;
  try {
    location = await deleteSession();
  }
  catch (error) {
    console.warn('[AppState] Sign-out failed:', error);
    return;
  }
  if (location) {
    if (isNavigableUrl(location)) {
      window.location.href = location;
    }
    else {
      console.warn('[AppState] Ignored sign-out redirect that is not http(s):', location);
    }
    return;
  }
  setUser(undefined);
  try {
    window.NX?.State?.setUser?.(undefined);
  }
  catch {
    // ExtJS not launched
  }
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function safely(getter) {
  try {
    return getter();
  }
  catch {
    return undefined;
  }
}

/**
 * Re-reads getter whenever subscribe reports a change.
 */
function useSubscribedValue(getter, subscribe, dependencies = []) {
  const [value, setValue] = useState(() => safely(getter));
  const getterRef = useRef(getter);
  getterRef.current = getter;

  useEffect(() => {
    const update = () => {
      const next = safely(getterRef.current);
      setValue((previous) => (previous === next ? previous : next));
    };
    const unsubscribe = subscribe(update);
    update();
    return unsubscribe;
  }, dependencies);

  return value;
}

/** Same contract as ExtJS.useState: re-evaluates getValue whenever any state value changes. */
export function useAppState(getValue) {
  return useSubscribedValue(getValue, (listener) => onStateChange(['changed'], listener));
}

/** Same contract as ExtJS.usePermission: re-evaluates when permissions or the user change. */
export function usePermission(getValue, dependencies) {
  return useSubscribedValue(getValue, (listener) => {
    const unsubscribePermissions = onPermissionsChange(listener);
    const unsubscribeUser = onStateChange(['userchanged'], listener);
    return () => {
      unsubscribePermissions();
      unsubscribeUser();
    };
  }, dependencies ?? []);
}

/**
 * Same contract as ExtJS.useVisiblityWithChanges: re-evaluates after any state or permission change and returns what
 * isVisibleMethod returns, unchanged. Callers such as useFilteredRoutes return a list of routes, not a boolean.
 */
export function useVisibilityWithChanges(isVisibleMethod) {
  return useSubscribedValue(isVisibleMethod, onAnyChange);
}

/** @returns {{id: string, authenticated: boolean, administrator: boolean, authenticatedRealms: string[]}|undefined} */
export function useUser() {
  return useAppState(() => getValue('user'));
}

/** @returns {{version: string, edition: string}} */
export function useStatus() {
  return useAppState(() => getValue('status'));
}

/** @returns {{daysToExpiry: number}} */
export function useLicense() {
  return useAppState(() => getValue('license'));
}

export default {
  STATE_SEED_VARIABLE,
  STATE_MACHINE_FLAG,
  waitForStateSeed,
  isEnabledBySeed,
  startAppState,
  followExtJsUser,
  stopAppState,
  isActive,
  getService,
  state,
  setUser,
  refreshNow,
  checkPermission,
  arePermissionsReady,
  waitForPermissions,
  waitForNextPermissionChange,
  hasUser,
  isAuthStateResolved,
  whenAuthStateResolved,
  isLicensedEdition,
  isProEdition,
  isEnterpriseEdition,
  onStateChange,
  onPermissionsChange,
  signOut,
  useState: useAppState,
  usePermission,
  useVisiblityWithChanges: useVisibilityWithChanges,
  useUser,
  useStatus,
  useLicense
};
